<template>
  <v-dialog :value="value" @input="$emit('input')" width="720px">
    <v-card>
      <v-card-title>
        <span class="headline">
          <v-icon left>mdi-file-document-multiple</v-icon>Payment Invoices
        </span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <div class="subtitle-2">{{ recordTitle }}</div>
          </v-col>
          <v-col cols="12">
            <input
              ref="invoiceInput"
              type="file"
              class="d-none"
              multiple
              accept="image/*,.pdf,.ppt,.pptx,.txt,.doc,.docx,.xls,.xlsx"
              @change="prepareUpload"
            />
            <v-btn color="secondary" small :disabled="loading" @click="$refs.invoiceInput.click()">
              <v-icon left small>mdi-cloud-upload</v-icon>Select Invoices
            </v-btn>
            <span class="ml-2 caption" v-if="files.length">{{ files.length }} selected</span>
          </v-col>
          <v-col cols="12" v-if="files.length">
            <v-chip
              v-for="(file, index) in files"
              :key="file.name + index"
              class="ma-1"
              close
              small
              @click:close="removeFile(index)"
            >
              {{ file.name }}
            </v-chip>
          </v-col>
          <v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="displayInvoices"
              :items-per-page="5"
              class="table-small elevation-1"
              no-data-text="No invoices uploaded"
            >
              <template v-slot:[`item.filename`]="{ item }">
                <a :href="item.url" target="_blank">{{ item.filename || 'Invoice' }}</a>
              </template>
              <template v-slot:[`item.source`]="{ item }">
                <v-chip small :color="item.deletable ? 'secondary' : 'grey lighten-1'" text-color="white">
                  {{ item.source }}
                </v-chip>
              </template>
              <template v-slot:[`item.timestamp`]="{ item }">
                {{ formatDate(item.timestamp || item.uploadedAt) }}
              </template>
              <template v-slot:[`item.action`]="{ item }">
                <v-btn class="ims-action" color="info" small :href="item.url" target="_blank">
                  <v-icon small>mdi-download</v-icon>
                </v-btn>
                <v-btn
                  v-if="item.deletable"
                  class="ims-action"
                  color="error"
                  small
                  :disabled="loading"
                  @click="deleteInvoice(item)"
                >
                  <v-icon small color="white">mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="red darken-1" :disabled="loading" @click="close">Close</v-btn>
        <v-btn text color="green darken-1" :disabled="!files.length || loading" @click="upload">
          Upload
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import campaignAPIs from '../Campaign/campaignAPIs'
import paymentAPIs from './paymentAPIs'

export default {
  name: 'PaymentInvoiceModal',
  props: {
    value: Boolean,
    payment: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      files: [],
      loading: false,
      headers: [
        { value: 'filename', text: 'File', sortable: true },
        { value: 'source', text: 'Source', sortable: true },
        { value: 'timestamp', text: 'Uploaded At', sortable: true },
        { value: 'action', text: 'Actions', sortable: false }
      ]
    }
  },
  computed: {
    invoiceItems() {
      return Array.isArray(this.payment.paymentInvoices) ? this.payment.paymentInvoices : []
    },
    displayInvoices() {
      const invoices = this.normalizedSignedInvoices
        .concat(this.normalizedUploadedInvoices)
        .concat(this.normalizedGeneratedInvoices)
        .filter(invoice => invoice.url)
      const urls = []
      return invoices.filter(invoice => {
        if (urls.includes(invoice.url)) return false
        urls.push(invoice.url)
        return true
      })
    },
    normalizedUploadedInvoices() {
      const paymentInvoices = this.invoiceItems.map((invoice, index) => this.normalizeInvoice(invoice, {
        fallbackName: `Uploaded Invoice ${index + 1}`,
        source: 'Uploaded',
        deletable: true,
        deleteType: 'payment'
      }))
      const reportInvoices = this.reportInvoiceItems.map((invoice, index) => this.normalizeInvoice(invoice, {
        fallbackName: `Uploaded Invoice ${paymentInvoices.length + index + 1}`,
        source: 'Uploaded',
        deletable: !!invoice.id,
        deleteType: 'report'
      }))
      return paymentInvoices.concat(reportInvoices)
    },
    normalizedSignedInvoices() {
      return this.signedInvoiceItems.map((invoice, index) => this.normalizeInvoice(invoice, {
        fallbackName: `Creator Signed Invoice ${index + 1}`,
        source: 'Creator Signed',
        deletable: false
      }))
    },
    normalizedGeneratedInvoices() {
      const invoices = []
      if (this.payment.invoice) {
        invoices.push(this.normalizeInvoice(this.payment.invoice, {
          fallbackName: 'Generated Invoice',
          source: 'Generated',
          deletable: false
        }))
      }
      return invoices
    },
    reportInvoiceItems() {
      return Array.isArray(this.payment.uploadInvoice) ? this.payment.uploadInvoice : []
    },
    signedInvoiceItems() {
      return Array.isArray(this.payment.signedInvoices) ? this.payment.signedInvoices : []
    },
    recordTitle() {
      const handle = this.payment.instagram ? `@${this.payment.instagram}` : ''
      return [this.payment.campaignName, handle].filter(Boolean).join(' - ')
    },
    paymentType() {
      if (this.payment.batchNo === 'imported') return 'imported'
      if (this.payment.paymentStatus === 1) return 'completed'
      if (this.payment.paymentStatus === 5 || this.payment.paymentStatus === 6) return 'finance'
      return 'pre-processing'
    }
  },
  watch: {
    value(val) {
      if (!val) this.files = []
    }
  },
  methods: {
    prepareUpload(e) {
      this.files = this.files.concat(Object.keys(e.target.files).map(i => e.target.files[i]))
      e.target.value = ''
    },
    removeFile(index) {
      this.files.splice(index, 1)
    },
    formatDate(date) {
      if (!date) return '-'
      return moment(date).format('DD/MM/YYYY hh:mm:ss A')
    },
    normalizeInvoice(invoice, options) {
      if (!invoice) {
        return Object.assign({}, options, { url: '' })
      }
      const isString = typeof invoice === 'string'
      const url = isString ? invoice : invoice.url
      return Object.assign({}, isString ? {} : invoice, {
        url,
        filename: isString ? options.fallbackName : (invoice.filename || invoice.name || options.fallbackName),
        source: options.source,
        deletable: options.deletable,
        deleteType: options.deleteType || null
      })
    },
    async upload() {
      try {
        this.loading = true
        const response = await paymentAPIs.uploadPaymentInvoices(this.paymentType, this.payment.id, this.files)
        this.loading = false
        if (response && Array.isArray(response.uploadInvoice)) {
          this.$set(this.payment, 'paymentInvoices', response.uploadInvoice)
          this.files = []
          this.$emit('updated')
          this.$swal({ text: 'Invoices uploaded', type: 'success' })
          return
        }
      } catch (error) {
        console.log(error)
        this.loading = false
      }
      this.$swal({ text: 'Error uploading invoices', type: 'error' })
    },
    deleteInvoice(invoice) {
      this.$swal({
        title: 'Delete Confirm!',
        text: 'Are you sure you want to delete this invoice?',
        confirmButtonText: 'Confirm',
        type: 'info',
        showCancelButton: true,
        showCloseButton: true
      }).then(async result => {
        if (!result.value) return
        try {
          this.loading = true
          const response = invoice.deleteType === 'report'
            ? await this.deleteReportInvoice(invoice)
            : await paymentAPIs.deletePaymentInvoice(this.paymentType, this.payment.id, invoice.id)
          this.loading = false
          if (response && (response.success || Array.isArray(response.uploadInvoice))) {
            if (Array.isArray(response.uploadInvoice)) {
              this.$set(this.payment, 'paymentInvoices', response.uploadInvoice)
            } else if (invoice.deleteType === 'payment') {
              this.$set(this.payment, 'paymentInvoices', this.invoiceItems.filter(item => {
                if (invoice.id && item.id) return item.id !== invoice.id
                return item.url !== invoice.url
              }))
            }
            if (invoice.deleteType === 'report') {
              this.$set(this.payment, 'uploadInvoice', this.reportInvoiceItems.filter(item => item.id !== invoice.id))
            }
            this.$emit('updated')
            this.$swal({ text: 'Invoice deleted', type: 'success' })
            return
          }
        } catch (error) {
          console.log(error)
          this.loading = false
        }
        this.$swal({ text: 'Error deleting invoice', type: 'error' })
      })
    },
    async deleteReportInvoice(invoice) {
      await campaignAPIs.bulkDeleteInvoices({ ids: [invoice.id] })
      await this.$store.dispatch('campaign/getUploadedInvoiceLists')
      return { success: true }
    },
    close() {
      this.$emit('input', false)
    }
  }
}
</script>



// WEBPACK FOOTER //
// PaymentInvoiceModal.vue?3fcb8822